import {
  AgentMessageEventLlmCallData,
  AgentMessageEventOutput,
  AgentMessageEventToolCallDataOutput,
  AgentMessageEventType,
  GuardrailStage,
} from '../../../gamma-api';

import { GuardrailCheckData, GuardrailEventData } from './guardrailChecks';

export interface GuardrailLlmGroup {
  key: string;
  llmCall?: AgentMessageEventLlmCallData;
  inputChecks: GuardrailCheckData[];
  outputChecks: GuardrailCheckData[];
}

export interface GuardrailToolCallItem {
  key: string;
  toolCall: AgentMessageEventToolCallDataOutput;
}

export enum GuardrailDetailsSection {
  Input = 'input',
  Llm = 'llm',
  Output = 'output',
}

export interface GuardrailDetailsTarget {
  groupKey: string;
  section: GuardrailDetailsSection;
  checkIndex?: number;
}

export const getGuardrailDetailsPanelKey = (groupKey: string, section: GuardrailDetailsSection): string =>
  `${groupKey}:${section}`;

export enum GuardrailFlowItemType {
  Llm = 'llm',
  Tool = 'tool',
}

export type GuardrailFlowItem =
  | { type: GuardrailFlowItemType.Llm; group: GuardrailLlmGroup }
  | { type: GuardrailFlowItemType.Tool; item: GuardrailToolCallItem };

export const buildGuardrailFlow = (events: AgentMessageEventOutput[]): GuardrailFlowItem[] => {
  const flow: GuardrailFlowItem[] = [];
  let pendingInputChecks: GuardrailCheckData[] = [];
  let currentLlmGroup: GuardrailLlmGroup | undefined;
  let generatedGroupIndex = 0;

  const appendGroup = (group: GuardrailLlmGroup) => {
    flow.push({ type: GuardrailFlowItemType.Llm, group });
    currentLlmGroup = group;
  };

  const flushPendingInputChecks = () => {
    if (!pendingInputChecks.length) {
      return;
    }
    appendGroup({
      key: `checks:${generatedGroupIndex++}`,
      inputChecks: pendingInputChecks,
      outputChecks: [],
    });
    pendingInputChecks = [];
  };

  for (const event of events) {
    if (event.data.event_type === AgentMessageEventType.Guardrail) {
      const checks = (event.data as GuardrailEventData).checks || [];
      const inputChecks = checks.filter((check) => check.stage === GuardrailStage.Input);
      const outputChecks = checks.filter((check) => check.stage === GuardrailStage.Output);

      pendingInputChecks.push(...inputChecks);
      if (outputChecks.length) {
        if (!currentLlmGroup) {
          appendGroup({
            key: `checks:${generatedGroupIndex++}`,
            inputChecks: [],
            outputChecks,
          });
        } else {
          currentLlmGroup.outputChecks.push(...outputChecks);
        }
      }
      continue;
    }

    if (event.data.event_type === AgentMessageEventType.LlmCall) {
      appendGroup({
        key: event.id,
        llmCall: event.data,
        inputChecks: pendingInputChecks,
        outputChecks: [],
      });
      pendingInputChecks = [];
      continue;
    }

    if (event.data.event_type === AgentMessageEventType.ToolCall) {
      flushPendingInputChecks();
      flow.push({
        type: GuardrailFlowItemType.Tool,
        item: {
          key: event.id,
          toolCall: event.data,
        },
      });
      currentLlmGroup = undefined;
    }
  }

  flushPendingInputChecks();
  return flow;
};
